import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { getEnv } from '@/lib/utils/env';
import { validateRoomAccess, validateRoomAccessByHash, getRoomPublicInfo } from '../services/roomValidationService';
import { decodeLinkHash, parseInviteParams } from '../services/linkService';
import { parseEmbedUrlParams, type EmbedParams } from '../lib/embed/route';
import { matchesAllowedOrigin } from '../lib/embed/originMatch';
import type { RoomAccess, InvitePrefillData } from '../types';
import { ButtonContent } from './ui/ButtonContent';
import BrandedLineLoader from './BrandedLineLoader';
import { RespondentHeader } from './conversation/RespondentHeader';
import { useAssetsReady } from '../lib/useAssetsReady';
import { LiquidEtherBackground } from './common/LiquidEtherBackground';
import { ConversationTheme } from './conversation/ConversationTheme';
import { ConversationIcon } from './icons/ConversationIcon';
import { captureEvent, captureException } from '../lib/telemetry/posthog/frontend';
import { buildLiveInterviewBaseProps } from '../lib/telemetry/live-interview-contract';

type JoinRejectedReason =
  | 'invalid_credentials'
  | 'link_invalid'
  | 'link_expired'
  | 'schedule_closed'
  | 'schedule_not_open'
  | 'room_unavailable'
  | 'network_error';

interface RoomJoinPageProps {
  /** When true, this is the iframe-side /embed/:hash route. */
  embedMode?: boolean;
}

export const RoomJoinPage: React.FC<RoomJoinPageProps> = ({ embedMode = false }) => {
  const { hash } = useParams<{ hash?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse embed params on mount when in embed mode. These are NOT PII so we
  // don't strip them from the URL bar; the iframe never appears in the
  // user's history anyway.
  const embedParamsRef = useRef<EmbedParams | null>(null);
  if (embedMode && embedParamsRef.current === null) {
    const searchParams = new URLSearchParams(location.search);
    embedParamsRef.current = parseEmbedUrlParams(searchParams);
  }
  const embedParams = embedParamsRef.current;

  // Parse invite prefill params AND the automation participant token from the
  // URL query string, then strip them immediately. Runs once per mount.
  const prefillRef = useRef<InvitePrefillData | null>(null);
  const participantTokenRef = useRef<string | null>(null);
  const queryParsedRef = useRef(false);
  if (!queryParsedRef.current) {
    queryParsedRef.current = true;
    const searchParams = new URLSearchParams(location.search);
    prefillRef.current = parseInviteParams(searchParams) ?? null;
    // Capture the participant token (`pt`) BEFORE stripping the URL. It is a
    // bearer credential (resolved server-side at create-response), so it must
    // never linger in browser history; it rides router state like `passcode`.
    participantTokenRef.current = searchParams.get('pt');

    // Strip PII + token from the URL bar immediately (browser history stays
    // clean). Skipped in embedMode because we want to keep `embed_origin`
    // accessible if the iframe re-reads it later.
    if ((prefillRef.current || participantTokenRef.current) && hash && !embedMode) {
      window.history.replaceState({}, '', `/join/${hash}`);
    }
  }
  const prefillData = prefillRef.current || undefined;
  const participantToken = participantTokenRef.current || undefined;

  // Check for state passed from redirect (e.g., from refresh on interview page)
  const locationState = location.state as {
    prefillRoomId?: string;
    message?: string;
    flowId?: string;
  } | null;

  // Seed `roomId` from the decoded hash on mount so the existing
  // `getRoomPublicInfo` effect fires in parallel with link validation. The
  // status call returns the workspace logo ahead of bcrypt-bound validation,
  // letting the respondent header render the workspace mark from the onset
  // rather than flashing the holito fallback first.
  const [roomId, setRoomId] = useState(() => {
    if (locationState?.prefillRoomId) return locationState.prefillRoomId;
    if (hash) {
      const decoded = decodeLinkHash(hash);
      if (decoded) return decoded.roomId;
    }
    return '';
  });
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!hash);
  const [error, setError] = useState<string | null>(locationState?.message || null);
  // Seed roomInfo synchronously from `window.__roomBoot` when the
  // `/join/[hash]` Pages Function inlined it into the HTML response.
  // Lets the loader paint the brand on the first frame. The public-status
  // refresh keeps that known branding when its request fails temporarily.
  const [roomInfo, setRoomInfo] = useState<{
    roomId: string;
    targetName?: string;
    defaultLanguage?: string;
    workspaceId?: string | null;
    workspaceLogoUrl?: string | null;
    workspaceIconUrl?: string | null;
    workspaceAccentColor?: string | null;
  } | null>(() => {
    if (typeof window === 'undefined') return null;
    const boot = window.__roomBoot;
    if (!boot || !hash) return null;
    const decoded = decodeLinkHash(hash);
    // Cross-check that the SSR'd boot payload matches the requested room
    // - if a respondent reuses a tab across rooms via SPA nav, stale
    // __roomBoot would otherwise leak into the wrong join screen.
    if (!decoded || decoded.roomId !== boot.roomId) return null;
    return {
      roomId: boot.roomId,
      targetName: boot.targetName ?? undefined,
      workspaceId: boot.workspaceId,
      workspaceLogoUrl: boot.workspaceLogoUrl,
      workspaceIconUrl: boot.workspaceIconUrl,
      workspaceAccentColor: boot.workspaceAccentColor ?? null,
    };
  });
  const [scheduleStatus, setScheduleStatus] = useState<'open' | 'not_yet_open' | 'closed' | null>(null);
  const [scheduleOpensAt, setScheduleOpensAt] = useState<string | null>(null);
  const [scheduleClosesAt, setScheduleClosesAt] = useState<string | null>(null);
  /**
   * True when the embed-mode allowlist gate refused the parent origin.
   * Renders the "embed not authorized" block panel instead of the join
   * form. Only set in embedMode; direct-link traffic bypasses the gate.
   */
  const [embedOriginBlocked, setEmbedOriginBlocked] = useState(false);

  const joinViewedEmittedRef = useRef(false);

  // Optional Cloudflare Turnstile. Ship-dark: the widget renders (and a
  // token is sent) ONLY when a site key is configured. When absent, nothing
  // renders and no token is forwarded, preserving the frictionless join.
  const turnstileSiteKey = getEnv('VITE_TURNSTILE_SITE_KEY', false) || '';
  const turnstileEnabled = turnstileSiteKey.length > 0;
  const turnstileRef = useRef<TurnstileInstance>(null);

  /**
   * Stable Turnstile options. Invisible + proactive `render` so the
   * challenge solves silently at mount and token retrieval at submit is a
   * cheap `getResponsePromise()` lookup (mirrors LoginPage).
   */
  const turnstileOptions = useRef(
    { theme: 'dark', size: 'invisible', execution: 'render' } as const,
  ).current;

  /**
   * Resolve the current Turnstile token, or undefined when Turnstile is not
   * configured (dark) or no widget instance is mounted yet. Failures resolve
   * to undefined so a token hiccup never blocks a legitimate join: the
   * server only enforces when its own secret is set.
   */
  const getTurnstileToken = async (): Promise<string | undefined> => {
    if (!turnstileEnabled) return undefined;
    try {
      const token = await turnstileRef.current?.getResponsePromise();
      return token || undefined;
    } catch {
      return undefined;
    }
  };

  // Defer the actual navigation until fonts + workspace logo bitmap are
  // paint-ready. The next page (interview / pre-interview form) renders
  // Inter text and the workspace logo; without this gate the user sees
  // FOUT (text reflows when fonts arrive) and a delayed logo "pop-in" on
  // the next page header. Validation completes asynchronously, so we stash
  // the access result and let an effect fire the navigate() once both
  // validation success AND assets are ready.
  const [pendingAccess, setPendingAccess] = useState<{
    access: RoomAccess;
    passcode: string;
  } | null>(null);
  const assetsReady = useAssetsReady(roomInfo?.workspaceLogoUrl ?? null);

  const trackJoinRejected = (reason: JoinRejectedReason, roomForFlow?: string) => {
    const rid = (roomForFlow || roomId).trim().toUpperCase();
    if (rid.length !== 8) {
      if (reason === 'link_invalid') {
        return;
      }
    }
    if (rid.length === 8) {
      captureEvent('live_interview.join_rejected', {
        ...buildLiveInterviewBaseProps(rid, 'join'),
        reason,
      });
    }
  };

  // If we have a hash, validate it automatically
  useEffect(() => {
    if (hash) {
      void validateLinkHash();
    }
  }, [hash]);

  // Asset-gated navigation: once we have a validated access + the brand
  // assets are paint-ready, fire the navigate(). Until then, the loader
  // stays on screen so the user never sees FOUT or a logo pop-in on the
  // next page.
  useEffect(() => {
    if (pendingAccess && assetsReady) {
      navigateToInterview(pendingAccess.access, pendingAccess.passcode);
    }
    // navigateToInterview is stable for our purposes (defined in render
    // closure but its observable behavior is deterministic given inputs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAccess, assetsReady]);

  useEffect(() => {
    const rid = roomId.trim().toUpperCase();
    if (rid.length !== 8 || joinViewedEmittedRef.current) {
      return;
    }
    joinViewedEmittedRef.current = true;
    captureEvent('live_interview.join_viewed', buildLiveInterviewBaseProps(rid, 'join'));
  }, [roomId]);

  const validateLinkHash = async () => {
    if (!hash) return;

    setValidating(true);
    setError(null);

    try {
      // Decode the hash first to get the passcode
      const decoded = decodeLinkHash(hash);
      if (!decoded) {
        trackJoinRejected('link_invalid');
        setError('Invalid link. Please check the URL or enter your room details manually.');
        setValidating(false);
        return;
      }

      // join_viewed is captured by the `roomId`-watching effect, which now
      // sees the seeded room ID on first render. Don't double-fire here.

      const turnstileToken = await getTurnstileToken();
      const access = await validateRoomAccessByHash(hash, turnstileToken);

      if (access) {
        // Embed-mode allowlist gate. NULL/empty `allowed_origins` means
        // "embeddable anywhere"; a populated list is enforced against the
        // parent origin. The server still owns the final word in
        // create-response, but blocking here gives the respondent a
        // clear visual instead of a silent failure mid-flow.
        if (embedMode && access.allowed_origins && access.allowed_origins.length > 0) {
          const parentOrigin = embedParams?.embedOrigin ?? '';
          if (!matchesAllowedOrigin(parentOrigin, access.allowed_origins)) {
            setEmbedOriginBlocked(true);
            setValidating(false);
            return;
          }
        }
        // Valid link. Schedule gating fires synchronously via
        // `navigateToInterview`'s pre-check; if blocked, we drop the
        // loader so the schedule view can render. Otherwise we stash the
        // access for the asset-gated effect below to fire the actual
        // navigate() once fonts + workspace logo are paint-ready.
        if (
          access.schedule_status === 'not_yet_open' ||
          access.schedule_status === 'closed'
        ) {
          // Reuse navigateToInterview to set the schedule view state.
          navigateToInterview(access, decoded.passcode);
          setValidating(false);
          return;
        }
        setPendingAccess({ access, passcode: decoded.passcode });
        return;
      }

      trackJoinRejected('link_expired', decoded.roomId);
      setRoomId(decoded.roomId);
      setError('This link may have expired. Please enter the passcode to continue.');
      setValidating(false);
    } catch (err) {
      const dec = hash ? decodeLinkHash(hash) : null;
      trackJoinRejected('network_error', dec?.roomId);
      captureException(err instanceof Error ? err : String(err), 'RoomJoinPage.validateLink', { hash });
      // Show specific error message from server
      const errorMsg = err instanceof Error ? err.message : 'Failed to validate link. Please try again.';
      // Pre-fill room ID if we decoded the hash
      const decoded = decodeLinkHash(hash);
      if (decoded) {
        setRoomId(decoded.roomId);
      }
      setError(errorMsg);
      setValidating(false);
    }
  };

  // Format a date string for display
  const formatScheduleDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Hand off to the interview view. Returns `true` when an actual navigation
   * is dispatched, `false` when schedule gating blocks the join - callers
   * use this to decide whether to keep the validating loader on screen
   * (true) or release it so the schedule message can render (false).
   */
  const navigateToInterview = (access: RoomAccess, validatedPasscode: string): boolean => {
    // Check schedule status before navigating
    if (access.schedule_status === 'not_yet_open' || access.schedule_status === 'closed') {
      trackJoinRejected(
        access.schedule_status === 'not_yet_open' ? 'schedule_not_open' : 'schedule_closed',
        access.roomId,
      );
      setScheduleStatus(access.schedule_status);
      setScheduleOpensAt(access.opens_at || null);
      setScheduleClosesAt(access.closes_at || null);
      return false;
    }

    // Embed-mode entitlement check: refuse to render the call surface when
    // the workspace plan does not grant embed_widget. UI hides the snippet
    // upstream and the server rejects create-response, but this is the
    // iframe-side belt-and-braces.
    if (embedMode && access.embed_widget === false) {
      setError('Embed not enabled for this workspace.');
      setValidating(false);
      return false;
    }

    captureEvent('live_interview.join_validated', buildLiveInterviewBaseProps(access.roomId, 'join'));

    // Construct InterviewContext from RoomAccess direct columns
    const context = {
      targetName: access.target_name,
      targetUrl: access.target_url,
      goal: access.goal,
      details: access.details || '',
      productResearch: access.product_research,
      outputLanguage: access.output_language,
      // Persisted language from resumed sessions
      interviewLanguage: access.interview_language,
      // Inputs for the join-screen time estimate
      fieldCount: access.field_count,
      probingDepth: access.probing_depth,
    };

    // Navigate to interview with access info in state
    // Note: passcode is still needed for headless create-response enrollment
    const embedSearch = embedParams ? new URLSearchParams({
      embed_origin: embedParams.embedOrigin,
      ...(embedParams.theme ? { embed_theme: embedParams.theme } : {}),
    }).toString() : '';
    void navigate(`/interview/${access.roomId}${embedSearch ? `?${embedSearch}` : ''}`, {
      state: {
        sessionId: access.sessionId,
        context,
        validated: true,
        roomId: access.roomId,
        passcode: validatedPasscode,
        videoEnabled: access.videoEnabled,
        requireVideo: access.requireVideo,
        screenShareEnabled: access.screenShareEnabled,
        // Historical campaign settings retained in navigation data
        requireName: access.require_name,
        requireEmail: access.require_email,
        // Enforced language for the interview
        enforceLanguage: access.enforce_language,
        // Org referral setting
        showHolitoReferral: access.show_holito_referral,
        // Workspace branding for standalone pages and conversation accents
        workspaceId: access.workspaceId ?? null,
        workspaceLogoUrl: access.workspaceLogoUrl ?? null,
        workspaceIconUrl: access.workspaceIconUrl ?? null,
        workspaceAccentColor: access.workspaceAccentColor ?? null,
        // Pre-fill data from invite link query params (for seamless join)
        prefillData,
        // Automation participant token (`pt`), forwarded to create-response so
        // the response links back to the participant and repeat clicks resume.
        participantToken,
        flowId: access.roomId,
        // Embed-mode flow: tells InterviewFlow to emit lifecycle
        // postMessage events and retain the host's theme on reload.
        embedParams,
        // Historical identity mode; current participation does not render
        // separate pre/post identity screens.
        identityCollection: access.identity_collection,
        conversationConfig: access.conversation_config,
        // Post-call behaviour. SuccessScreen reads these directly. Embed
        // redirects route through the snippet via postMessage (handled in
        // SuccessScreen when embedOrigin is present).
        completionAction: access.completion_action ?? 'message',
        completionMessage: access.completion_message ?? null,
        completionRedirectUrl: access.completion_redirect_url ?? null,
      },
    });
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roomId || !passcode) return;

    captureEvent('live_interview.join_submitted', buildLiveInterviewBaseProps(roomId.toUpperCase(), 'join'));

    setLoading(true);
    setError(null);

    try {
      const turnstileToken = await getTurnstileToken();
      const access = await validateRoomAccess(roomId.toUpperCase(), passcode, turnstileToken);

      if (access) {
        // Same asset-gating contract as the hash flow: stash the access
        // so the effect below fires navigate() once fonts + logo are
        // paint-ready. Button keeps its loading dots visible through the
        // wait. Schedule gating remains synchronous because the user
        // must see the schedule view immediately.
        if (
          access.schedule_status === 'not_yet_open' ||
          access.schedule_status === 'closed'
        ) {
          navigateToInterview(access, passcode);
          setLoading(false);
          return;
        }
        setPendingAccess({ access, passcode });
        return;
      }

      trackJoinRejected('invalid_credentials');
      setError('Invalid room ID or passcode. Please check your details and try again.');
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message.toLowerCase() : '';
      const reason: JoinRejectedReason = errorMessage.includes('invalid')
        ? 'invalid_credentials'
        : 'network_error';
      trackJoinRejected(reason);
      captureException(err instanceof Error ? err : String(err), 'RoomJoinPage.joinRoom', { roomId });
      // Show specific error message from server
      const errorMsg = err instanceof Error ? err.message : 'Failed to join room. Please try again.';
      setError(errorMsg);
      setLoading(false);
    }
  };

  // Check room exists when room ID changes (for showing subject name and default language)
  useEffect(() => {
    let cancelled = false;
    const requestedRoomId = roomId.toUpperCase();
    setRoomInfo(previous => previous?.roomId === requestedRoomId ? previous : null);
    const checkRoom = async () => {
      if (roomId.length === 8) {
        const info = await getRoomPublicInfo(requestedRoomId);
        if (cancelled || info?.serverError) return;
        if (info?.exists && info.status === 'active') {
          setRoomInfo({
            roomId: requestedRoomId,
            targetName: info.targetName,
            defaultLanguage: info.interviewLanguage,
            workspaceId: info.workspaceId ?? null,
            workspaceLogoUrl: info.workspaceLogoUrl ?? null,
            workspaceIconUrl: info.workspaceIconUrl ?? null,
            workspaceAccentColor: info.workspaceAccentColor ?? null,
          });
          // Set the default language from the room if available
          /* if (info.interviewLanguage) {
             setSelectedLanguage(info.interviewLanguage);
          } */
        } else {
          setRoomInfo(null);
        }
      } else {
        setRoomInfo(null);
      }
    };

    void checkRoom();
    return () => { cancelled = true; };
  }, [roomId]);

  const inputClasses =
    'w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-subtle-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors text-center text-lg tracking-wider uppercase';

  // Loading shows one workspace mark above the bar and reserves the destination
  // header geometry. Unknown branding uses the local holito mark.
  const loaderLogoUrl =
    roomInfo === null ? undefined : roomInfo.workspaceLogoUrl ?? null;

  if (validating) {
    return (
      <ConversationTheme accentColor={roomInfo?.workspaceAccentColor} className="h-screen bg-background text-foreground flex flex-col overflow-hidden" style={{ height: '100dvh' }}>
        {turnstileEnabled && <Turnstile ref={turnstileRef} siteKey={turnstileSiteKey} options={turnstileOptions} />}
        {!embedMode && <RespondentHeader />}
        <main className="flex-1 min-h-0 flex items-center justify-center">
          <BrandedLineLoader logoUrl={loaderLogoUrl ?? null} alt={roomInfo?.targetName ? `${roomInfo.targetName} logo` : 'Workspace logo'} />
        </main>
      </ConversationTheme>
    );
  }

  // Embed-mode allowlist gate: parent origin is not on the campaign's
  // `allowed_origins`. Minimal block panel, no CTAs (per UI rules).
  if (embedOriginBlocked) {
    return (
      <ConversationTheme accentColor={roomInfo?.workspaceAccentColor}
        data-hollie-embed-block
        className="min-h-screen bg-background flex flex-col items-center justify-center text-center px-6"
      >
        <h2 className="text-base font-semibold text-foreground lowercase">
          embed not authorized
        </h2>
        <p className="mt-2 text-sm text-muted-foreground lowercase">
          this domain is not on the campaign's allowed list.
        </p>
      </ConversationTheme>
    );
  }

  // Show schedule status messages instead of the form
  if (scheduleStatus === 'not_yet_open' || scheduleStatus === 'closed') {
    return (
      <ConversationTheme accentColor={roomInfo?.workspaceAccentColor} className="min-h-screen bg-background flex items-center justify-center px-4 py-8 md:p-4 relative overflow-hidden">
        <LiquidEtherBackground isVisible={!embedMode} />
        <div className="w-full max-w-md relative z-10">
          {!embedMode && <div className="text-center mb-10">
            <div className="flex justify-center mb-8">
              <a
                href="https://holito.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/holito-logo-black.svg"
                  alt="holito"
                  className="h-10 w-auto dark:invert"
                />
              </a>
            </div>
          </div>}

          <div className="animated-border-wrapper">
            <div className="glass-card p-6 md:p-8">
              <div className="text-center space-y-4">
                {scheduleStatus === 'not_yet_open' ? (
                  <>
                    <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-volt/20">
                      <svg className="w-8 h-8 text-accent-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-foreground">Not open yet</h2>
                    <p className="text-muted-foreground">
                      This isn't open yet.
                      {scheduleOpensAt && (
                        <> It opens on <span className="text-foreground font-medium">{formatScheduleDate(scheduleOpensAt)}</span>.</>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-danger/20">
                      <svg className="w-8 h-8 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-foreground">No longer available</h2>
                    <p className="text-muted-foreground">
                      No longer accepting responses.
                      {scheduleClosesAt && (
                        <> Closed on <span className="text-foreground font-medium">{formatScheduleDate(scheduleClosesAt)}</span>.</>
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {!embedMode && <p className="text-center text-base text-muted-foreground mt-8">
            Have an account?{' '}
            <a
              href="/login"
              className="text-accent-soft hover:underline"
            >
              Sign in
            </a>
          </p>}
        </div>
      </ConversationTheme>
    );
  }

  return (
    <ConversationTheme accentColor={roomInfo?.workspaceAccentColor} className="min-h-screen bg-background flex items-center justify-center px-4 py-8 md:p-4 relative overflow-hidden">
      <LiquidEtherBackground isVisible={!embedMode} />
      <div className="w-full max-w-md relative z-10">
        {/* Header - workspace wide logo replaces the holito BETA mark when present */}
        {!embedMode && <div className="text-center mb-10">
          <div className="flex justify-center mb-8">
            {roomInfo?.workspaceLogoUrl ? (
              <img
                src={roomInfo.workspaceLogoUrl}
                alt={roomInfo.targetName ? `${roomInfo.targetName} logo` : 'Workspace logo'}
                className="h-12 w-auto max-w-[240px] object-contain"
                draggable={false}
              />
            ) : (
              <a
                href="https://holito.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/holito-logo-black.svg"
                  alt="holito"
                  className="h-10 w-auto dark:invert"
                />
              </a>
            )}
          </div>
        </div>}

        {/* Form Card */}
        <div className="animated-border-wrapper">
          <div className="glass-card p-6 md:p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 text-sm text-foreground bg-wash-danger rounded-lg">
              {error}
            </div>
          )}

          {/* Room Info Preview - name styled prominently; label dropped per minimal-UI rule. */}
          {roomInfo?.targetName && (
            <div className="mb-6 p-4 bg-wash-accent rounded-lg text-center">
              <p className="text-foreground font-bold">{roomInfo.targetName}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="room-id" className="block text-sm font-medium text-muted-foreground mb-2">
                Room ID
              </label>
              <input
                id="room-id"
                type="text"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value.toUpperCase().slice(0, 8))}
                placeholder="XXXXXXXX"
                className={inputClasses}
                maxLength={8}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="passcode" className="block text-sm font-medium text-muted-foreground mb-2">
                Passcode
              </label>
              <input
                id="passcode"
                type="text"
                value={passcode}
                // DUAL-ACCEPT: new codes are 8 chars from the unambiguous
                // Crockford alphabet (uppercased here); legacy codes are
                // 6 numeric digits, which also survive this filter. Strip
                // anything outside [A-Z0-9] and cap at 8.
                onChange={(e) =>
                  setPasscode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))
                }
                placeholder="XXXXXXXX"
                className={inputClasses}
                maxLength={8}
                required
                disabled={loading}
              />
            </div>

            {/* Language Selection Removed */}

            <button
              type="submit"
              // DUAL-ACCEPT: enable for a legacy 6-digit code or a new
              // 8-char code. In-between lengths stay disabled.
              aria-busy={loading}
              disabled={loading || roomId.length !== 8 || (passcode.length !== 6 && passcode.length !== 8)}
              className="w-full py-4 button-accent font-bold text-lg rounded-lg transition-all duration-300 disabled:bg-surface-raised disabled:shadow-none disabled:cursor-not-allowed disabled:text-muted-foreground flex items-center justify-center gap-2"
            >
              <ButtonContent loading={loading}>
                <ConversationIcon className="w-5 h-5" />
                Join conversation
              </ButtonContent>
            </button>
          </form>
          </div>
        </div>

        {/* Footer */}
        {!embedMode && <p className="text-center text-base text-muted-foreground mt-8">
          Have an account?{' '}
          <a
            href="/login"
            className="text-accent-soft hover:underline"
          >
            Sign in
          </a>
        </p>}
      </div>

      {/* Optional invisible Turnstile - renders only when a site key is
          configured (ship-dark). Solves proactively so the token is ready
          by submit time. */}
      {turnstileEnabled && (
        <Turnstile
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          options={turnstileOptions}
        />
      )}
    </ConversationTheme>
  );
};

export default RoomJoinPage;
